import React, { useMemo, useState, useCallback, useEffect, FC } from "react";

import { Add } from "@material-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  DropdownFilter,
  makeStyles,
  StatusProps,
  Typography,
} from "@portex-pro/ui-components";
import { useLazyGetPartnersRequestQuery, usePaginatedGetPartnersRequestQuery } from "api/rest/partners/partnersApi";
import { ContactType } from "api/rest/partners/types/domain";
import { useCreateTagMutation } from "api/rest/tags/createTag";
import { useFindManyTagsQuery } from "api/rest/tags/findManyTags";
import { Tag } from "api/rest/tags/types";
import CursorPaginationTableControlsView from "components/pagination/CursorPaginationTableControlsView";
import PartnerTagView from "components/PartnerTagView";
import ScrollableView from "components/ScrollableView";
import { TableViewV2, TableViewColumnV2 } from "components/TableViewV2";
import compact from "lodash/compact";
import map from "lodash/map";
import sortBy from "lodash/sortBy";
import { useSnackbar } from "notistack";
import AddTagDialogView from "pages/shipper/components/tags/AddTagDialogView";
import TagView from "pages/shipper/components/tags/TagView";
import { useTranslation } from "react-i18next";
import { Sentry } from "sentry";
import { getUserFullName } from "utils/user";

import { EMPTY_CELL_HYPHEN } from "../../constants";

// Type for the render props pattern
export interface PartnerTableRenderProps {
  partners: ContactType[];
  isLoading: boolean;
  isFetching: boolean;
  selectedPartnerIds: Set<string>;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSelectionChange: (ids: Set<string>) => void;
  onSelectAll: (checked: boolean) => void;
  onSelectPartner: (partnerId: number, checked: boolean) => void;
  isChecked: (partner: ContactType) => boolean;
  allSelected: boolean;
  indeterminate: boolean;
}

export interface PartnerTableProps {
  // Data fetching
  partnerType?: "external" | "internal";
  skip?: boolean;

  // Selection - can be controlled or uncontrolled
  selectedPartnerIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>, partners: ContactType[]) => void;

  // Search - can be controlled or uncontrolled
  searchValue?: string;
  onSearchChange?: (value: string) => void;

  // Layout slots using children pattern
  children?: (props: PartnerTableRenderProps) => React.ReactNode;
  headerSlot?: React.ReactNode | ((props: PartnerTableRenderProps) => React.ReactNode);
  searchSlot?: React.ReactNode | ((props: PartnerTableRenderProps) => React.ReactNode);
  actionsSlot?: React.ReactNode | ((props: PartnerTableRenderProps) => React.ReactNode);
  footerSlot?: React.ReactNode | ((props: PartnerTableRenderProps) => React.ReactNode);

  // Table customization
  tableHeight?: string;
  wrapInScrollable?: boolean;
  showPagination?: boolean;

  // Column configuration
  showTags?: boolean;
  showLastBooked?: boolean;
  additionalColumns?: TableViewColumnV2<ContactType>[];

  // Callbacks
  onPartnersLoaded?: (partners: ContactType[]) => void;
  onError?: (error: unknown) => void;

  // Advanced features
  enableSelectAll?: boolean;
  prefetchAllPartners?: boolean;
  onSelectAll?: (checked: boolean) => void;

  // Component overrides
  tableClassName?: string;
  containerClassName?: string;
}

const SHOW_LAST_BOOKED_COLUMN = false;

const useStyles = makeStyles((theme) => ({
  items: {
    overflow: "auto",
    maxHeight: "20rem",
    "& .MuiMenuItem-root": {
      padding: theme.spacing(0.3, 1.5),
    },
  },
  addNewTagContainer: {
    width: "100%",
    position: "sticky",
    backgroundColor: "white",
    bottom: "0px",
  },
}));

type TagsFilterViewProps = {
  tags: Tag[];
  tagOptions: Tag[];
  onChange?: (tags: Tag[]) => void;
  onClickAddNewTag?: () => void;
};

export type TagPalette = Extract<StatusProps["palette"], "grey" | "green" | "blue" | "purple">;

export const getTagPalette = (tag: Tag): TagPalette => {
  let palette: TagPalette = "grey";

  switch (tag.tag) {
    case "FTL":
      palette = "green";
      break;
    case "LTL":
      palette = "green";
      break;
    case "FCL":
      palette = "blue";
      break;
    case "Air":
      palette = "purple";
  }

  return palette;
};

const tagGroupMap: Record<string, number> = {
  FTL: 0,
  LTL: 0,
  FCL: 1,
  Air: 2,
};

export const getTagGroup = (tag: Tag): number | null => {
  if (tagGroupMap.hasOwnProperty(tag.tag)) {
    return tagGroupMap[tag.tag];
  }

  return null;
};

const TagsFilterView: FC<TagsFilterViewProps> = ({ tags, tagOptions, onChange, onClickAddNewTag }) => {
  const { t } = useTranslation("shipper");
  const classes = useStyles();

  const options = useMemo(() => {
    const sortedOptions = sortBy(tagOptions, (tag) => getTagGroup(tag));

    return map(sortedOptions, (tag) => {
      const palette = getTagPalette(tag);
      return { label: <TagView palette={palette}>{tag.tag}</TagView>, value: tag.id };
    });
  }, [tagOptions]);

  const value = useMemo(() => map(tags, "id"), [tags]);
  const handleChange = useCallback(
    (value: (string | number)[]) => {
      onChange?.(tagOptions.filter((o) => value.includes(o.id)));
    },
    [onChange, tagOptions]
  );

  const placeholder = useMemo(() => {
    let placeholder = t("shipperTags.tagsFilterView.placeholder");
    if (tags.length) {
      placeholder += ` (${tags.length})`;
    }
    return placeholder;
  }, [t, tags.length]);

  return (
    <DropdownFilter
      placeholder={placeholder}
      multiple
      showSelectAllOption
      selectAllLabel={
        <Typography>
          <strong>{t("shipperTags.tagsFilterView.selectAllOption")}</strong>
        </Typography>
      }
      value={value}
      onChange={handleChange}
      PaperProps={{
        className: classes.items,
      }}
      options={options}
    >
      {({ onClose: _onClose }) => {
        if (!onClickAddNewTag) return null;

        return [
          <Box className={classes.addNewTagContainer} key="tags-filter-view-add-button">
            <Divider />
            <Button startIcon={<Add />} fullWidth variant={"text"} color={"primary"} onClick={onClickAddNewTag}>
              {t("shipperTags.tagsFilterView.addButton")}
            </Button>
          </Box>,
        ];
      }}
    </DropdownFilter>
  );
};

export const PartnerTable: React.FC<PartnerTableProps> = ({
  partnerType = "external",
  skip = false,
  selectedPartnerIds: controlledSelectedIds,
  onSelectionChange,
  searchValue: controlledSearchValue,
  onSearchChange: controlledOnSearchChange,
  children,
  headerSlot,
  searchSlot,
  actionsSlot,
  footerSlot,
  tableHeight,
  wrapInScrollable = false,
  showPagination = false,
  showTags = true,
  showLastBooked = SHOW_LAST_BOOKED_COLUMN,
  additionalColumns = [],
  onPartnersLoaded,
  enableSelectAll = true,
  onSelectAll: customOnSelectAll,
  tableClassName,
  containerClassName,
}) => {
  const { t } = useTranslation(["common", "shipper"]);
  const { enqueueSnackbar } = useSnackbar();

  // Handle controlled/uncontrolled search
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const searchValue = controlledSearchValue !== undefined ? controlledSearchValue : internalSearchValue;
  const handleSearchChange = useCallback(
    (value: string) => {
      if (controlledOnSearchChange) {
        controlledOnSearchChange(value);
      } else {
        setInternalSearchValue(value);
      }
    },
    [controlledOnSearchChange]
  );

  // Handle controlled/uncontrolled selection
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set());
  const selectedPartnerIds = controlledSelectedIds !== undefined ? controlledSelectedIds : internalSelectedIds;

  // Fetch partners
  const { data, isLoading, isFetching, ...paginationOptions } = usePaginatedGetPartnersRequestQuery(
    {
      queryParams: { partnerType, search: searchValue, tags: selectedTags.map((tag) => tag.id) },
    },
    { skip }
  );

  // Fetch tags
  const { data: tagsData } = useFindManyTagsQuery({ queryParams: {} });
  const [createTag, { isLoading: isCreatingTag }] = useCreateTagMutation();

  const [requestPartners] = useLazyGetPartnersRequestQuery();

  const handleCreateTag = async (tag: string) => {
    try {
      await createTag({ tag }).unwrap();
    } catch (e) {
      Sentry.captureException(e);
      enqueueSnackbar(t("common:errors.generic"), { variant: "error" });
    }
  };

  const partners = useMemo(() => {
    return data?.data.partners ?? [];
  }, [data?.data.partners]);

  const totalPartners = useMemo(() => {
    return data?.total ?? 0;
  }, [data]);

  // Notify parent when partners are loaded
  useEffect(() => {
    if (partners.length > 0 && onPartnersLoaded) {
      onPartnersLoaded(partners);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners]);

  // Selection handlers
  const handleSelectPartner = useCallback(
    (partnerId: number, checked: boolean) => {
      const newSelected = new Set(selectedPartnerIds);
      const partnerIdStr = partnerId.toString();

      if (checked) {
        newSelected.add(partnerIdStr);
      } else {
        newSelected.delete(partnerIdStr);
      }

      if (onSelectionChange) {
        onSelectionChange(newSelected, partners);
      } else {
        setInternalSelectedIds(newSelected);
      }
    },
    [selectedPartnerIds, onSelectionChange, partners]
  );

  const allSelected = partners.length > 0 && selectedPartnerIds.size === totalPartners;
  const indeterminate = selectedPartnerIds.size > 0 && !allSelected;

  const handleSelectAll = useCallback(
    async (checked: boolean) => {
      if (customOnSelectAll) {
        customOnSelectAll(checked);
      } else {
        if (checked && (indeterminate || selectedPartnerIds.size === 0)) {
          const {
            data: { partners },
          } = await requestPartners({
            queryParams: { partnerType, search: searchValue, tags: selectedTags.map((tag) => tag.id) },
          }).unwrap();

          const newSet = new Set(partners.map((p) => p.id.toString()));

          if (onSelectionChange) {
            onSelectionChange(newSet, partners);
          } else {
            setInternalSelectedIds(newSet);
          }
        } else {
          const newSet = new Set<string>();

          if (onSelectionChange) {
            onSelectionChange(newSet, partners);
          } else {
            setInternalSelectedIds(newSet);
          }
        }
      }
    },
    [
      partners,
      onSelectionChange,
      customOnSelectAll,
      requestPartners,
      searchValue,
      indeterminate,
      selectedPartnerIds.size,
      partnerType,
      selectedTags,
    ]
  );

  const isChecked = useCallback(
    (partner: ContactType) => selectedPartnerIds.has(partner.id.toString()),
    [selectedPartnerIds]
  );

  // Table columns
  const tableColumns: TableViewColumnV2<ContactType>[] = useMemo(() => {
    const columns: TableViewColumnV2<ContactType>[] = [
      {
        key: "company",
        title: t("shipper:company"),
        renderCell: (partner) => (
          <div>
            <div className="text-sm font-medium text-gray-900">{partner.company_name || EMPTY_CELL_HYPHEN}</div>
            <div className="text-xs text-gray-500">
              {compact([partner.city, partner.state]).join(", ") || EMPTY_CELL_HYPHEN}
            </div>
          </div>
        ),
      },
      {
        key: "contact",
        title: t("shipper:contact"),
        renderCell: (partner) => {
          const fullName = getUserFullName({ user: partner });
          return (
            <div>
              <div className="text-sm font-medium text-gray-900">{fullName || EMPTY_CELL_HYPHEN}</div>
              <div className="text-xs text-gray-500 break-all">{partner.user.email}</div>
            </div>
          );
        },
      },
    ];

    if (showTags) {
      columns.push({
        key: "tags",
        title: t("shipper:tags"),
        renderCell: (partner) => <PartnerTagView tags={partner.tags} />,
      });
    }

    if (showLastBooked) {
      columns.push({
        key: "lastBooked",
        title: "LAST BOOKED",
        renderCell: () => (
          <div className="flex items-center text-sm">
            <span className="text-gray-500">Never</span>
          </div>
        ),
        cellClassName: "whitespace-nowrap",
      });
    }

    return [...columns, ...additionalColumns];
  }, [t, showTags, showLastBooked, additionalColumns]);

  // Render props for children
  const renderProps: PartnerTableRenderProps = {
    partners,
    isLoading,
    isFetching,
    selectedPartnerIds,
    searchValue,
    onSearchChange: handleSearchChange,
    onSelectionChange: (ids: Set<string>) => {
      if (onSelectionChange) {
        onSelectionChange(ids, partners);
      } else {
        setInternalSelectedIds(ids);
      }
    },
    onSelectAll: handleSelectAll,
    onSelectPartner: handleSelectPartner,
    isChecked,
    allSelected,
    indeterminate,
  };

  // Render slot helper
  const renderSlot = (slot: React.ReactNode | ((props: PartnerTableRenderProps) => React.ReactNode)) => {
    if (!slot) return null;
    return typeof slot === "function" ? slot(renderProps) : slot;
  };

  // If children is provided, use it for custom rendering
  if (children) {
    return <>{children(renderProps)}</>;
  }

  // Default table rendering
  const tableElement = (
    <TableViewV2
      items={partners}
      columns={tableColumns}
      isLoading={isLoading || isFetching}
      rowKey="id"
      isRowSelected={isChecked}
      onClickRow={(partner) => {
        handleSelectPartner(partner.id, !isChecked(partner));
      }}
      headerStartingIcon={
        enableSelectAll ? (
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            onChange={(e) => handleSelectAll(e.target.checked)}
            aria-label="Select all partners"
            style={{ color: indeterminate ? "grey" : undefined }}
          />
        ) : undefined
      }
      startingIcon={(partner) => (
        <Checkbox
          checked={isChecked(partner)}
          onChange={(e) => {
            e.stopPropagation();
            handleSelectPartner(partner.id, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select partner ${partner.company_name}`}
        />
      )}
      startingIconHeaderCellProps={{
        style: { width: "56px" },
        className: "text-center",
      }}
      startingIconCellProps={() => ({
        style: { width: "56px" },
        className: "text-center",
      })}
    />
  );

  const tableContent = wrapInScrollable ? (
    <ScrollableView>{tableElement}</ScrollableView>
  ) : tableHeight ? (
    <div style={{ maxHeight: tableHeight, overflow: "auto" }} className={tableClassName}>
      {tableElement}
    </div>
  ) : (
    tableElement
  );

  return (
    <div className={containerClassName}>
      {renderSlot(headerSlot)}
      {(searchSlot || actionsSlot) && (
        <div className="flex justify-between items-center p-4">
          <div className="flex gap-4 items-center">
            {renderSlot(searchSlot)}
            <AddTagDialogView
              open={isTagDialogOpen}
              onClose={() => setIsTagDialogOpen(false)}
              loading={isCreatingTag}
              onAddTag={handleCreateTag}
            />
            <TagsFilterView
              tagOptions={tagsData?.data.tags || []}
              tags={selectedTags}
              onChange={(tags) => setSelectedTags(tags)}
              onClickAddNewTag={() => setIsTagDialogOpen(true)}
            />
          </div>
          <div>{renderSlot(actionsSlot)}</div>
        </div>
      )}
      {tableContent}
      {showPagination && paginationOptions.paginationOptions && (
        <CursorPaginationTableControlsView {...paginationOptions.paginationOptions} />
      )}
      {renderSlot(footerSlot)}
    </div>
  );
};

export default PartnerTable;
